import { useState } from "react";

import AnimatedLoader from "@hl/base-components/lib/AnimatedLoader";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { useAuth } from "@hl/shared-features/lib/features/auth";
import { WatchlistTile } from "@hl/shared-features/lib/features/watchlist-and-follow/watchlist/WatchlistTile";
import { useWatchlist } from "@hl/shared-features/lib/features/watchlist-and-follow/watchlist/useWatchlist";
import { useGetWatchlistCollectionsQuery } from "@hl/shared-features/lib/features/watchlist-and-follow/watchlist/watchlist.graphql.generated";
import { toStandardCollectionId } from "@hl/shared-features/lib/utils/blockchain";
import {
  Box,
  Button,
  Card,
  Center,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDidUpdate, useDocumentVisibility } from "@mantine/hooks";

export const UserWatchlistTab = () => {
  const auth = useAuth();
  const { isMedium } = useMantineMediaQueries({
    withInitialValues: true,
  });

  const watchlistQueryResult = useWatchlist();
  const { refetch } = watchlistQueryResult;
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const watchlistItems = watchlistQueryResult.data?.items?.edges;
  const { watch, isWatching } = watchlistQueryResult;

  // Handle watch actions on other tabs, then
  // refetching when focusing back on this tab
  const documentState = useDocumentVisibility();
  useDidUpdate(() => {
    if (documentState === "visible") {
      refetch();
    }
  }, [documentState, refetch]);

  const getWatchlistCollectionsQueryResult = useGetWatchlistCollectionsQuery({
    variables: {
      params: {
        ids: (watchlistItems ?? [])
          .slice(0, pageSize)
          .map((item) => item.value),
      },
    },
    skip: watchlistItems == null || watchlistItems.length === 0,
  });

  const resolvedCollections =
    getWatchlistCollectionsQueryResult.data?.getWatchlistCollections;
  const loading =
    watchlistQueryResult.loading || getWatchlistCollectionsQueryResult.loading;
  const hasMore = (watchlistItems?.length ?? 0) > pageSize * page;

  if (!auth.authenticated) {
    return (
      <Box>
        <Text>Watchlist</Text>
        <Text>You need to be signed in to view your watchlist.</Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Center mih={300}>
        <AnimatedLoader size={100} />
      </Center>
    );
  }

  const hasItems =
    resolvedCollections != null && resolvedCollections.length > 0;

  if (!hasItems) {
    return (
      <Box py={{ base: 24, sm: 0 }}>
        <Card py={40} w="100%">
          <Stack spacing={16} align="center">
            <Title order={6} fw={500}>
              No items in your watchlist
            </Title>
            <Text size="sm" mb={16} align="center">
              Add collections to your watchlist to keep track of them
            </Text>
          </Stack>
        </Card>
      </Box>
    );
  }

  const handleLoadMore = () => {
    const start = pageSize * page;
    const end = start + pageSize;
    const newIds =
      watchlistItems?.slice(start, end).map((item) => item.value) ?? [];

    getWatchlistCollectionsQueryResult.fetchMore({
      variables: {
        params: {
          ids: newIds,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const dedup = new Set<string>();
        fetchMoreResult!.getWatchlistCollections = [
          ...(previousResult?.getWatchlistCollections ?? []),
          ...(fetchMoreResult?.getWatchlistCollections ?? []),
        ].filter((item) => {
          if (dedup.has(item.id ?? "")) {
            return false;
          }
          dedup.add(item.id ?? "");
          return true;
        });

        return fetchMoreResult;
      },
    });
  };

  return (
    <Box pt={{ base: 16, sm: 0 }} pb={42}>
      <Stack spacing={40}>
        <SimpleGrid
          cols={3}
          breakpoints={[
            { maxWidth: "xl", cols: 2 },
            { maxWidth: "md", cols: 1 },
          ]}
          spacing={isMedium ? 40 : 24}
        >
          {resolvedCollections.slice(0, pageSize * page).map((collection) => {
            const standarizedOnchainId = toStandardCollectionId(collection.id);

            if (standarizedOnchainId == null) {
              return null;
            }
            const watching = isWatching(standarizedOnchainId);

            return (
              <WatchlistTile
                key={standarizedOnchainId}
                resolvedCollection={collection}
                isWatching={watching}
                onWatchClick={async () => {
                  await watch(standarizedOnchainId, !watching);
                }}
              />
            );
          })}
        </SimpleGrid>
        {hasMore && (
          <Center>
            <Button
              color="primary"
              onClick={() => {
                setPage(page + 1);
                handleLoadMore();
              }}
            >
              Load more
            </Button>
          </Center>
        )}
      </Stack>
    </Box>
  );
};
